import React, { useState } from 'react';
import { X, AlertCircle, CheckCircle } from 'lucide-react';
import { supabase } from '../utils/supabase';
import { useTranslation } from '../utils/translations';

interface ReportIssueModalProps {
  lessonId: string;
  lessonTitle: string;
  userId: string;
  nativeLanguage?: string;
  onClose: () => void;
}

const issueTypes = [
  { value: 'content_error', label: 'Content Error' },
  { value: 'audio_issue', label: 'Audio Issue' },
  { value: 'translation_error', label: 'Translation Error' },
  { value: 'technical_issue', label: 'Technical Issue' },
  { value: 'other', label: 'Other' }
];

export function ReportIssueModal({ lessonId, lessonTitle, userId, nativeLanguage, onClose }: ReportIssueModalProps) {
  const { t } = useTranslation(nativeLanguage);
  const [issueType, setIssueType] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!issueType || !description.trim()) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const { error: submitError } = await supabase
        .from('lesson_issue_reports')
        .insert({
          user_id: userId,
          lesson_id: lessonId,
          issue_type: issueType,
          description: description.trim()
        });

      if (submitError) throw submitError;

      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Error submitting report:', err);
      setError(t('Failed to submit report. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">{t('Thank you for your report!')}</h3>
          <p className="text-gray-600">{t('Your feedback helps us improve the lessons.')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">{t('Report an Issue')}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          {t('Help us improve this lesson by reporting any issues you found.')}
        </p>

        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-sm font-medium text-gray-700">{lessonTitle}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('Issue Type')}
            </label>
            <select
              value={issueType}
              onChange={(e) => setIssueType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              required
            >
              <option value="">{t('Select issue type')}</option>
              {issueTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {t(type.label as any)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('Description')}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('Please describe the issue in detail...')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 h-32 resize-none"
              required
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={submitting}
            >
              {t('Cancel')}
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={submitting || !issueType || !description.trim()}
            >
              {submitting ? t('Submitting...') : t('Submit Report')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
